import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  AUTH_FLOW_LOGOUT_ROUTES,
  AUTH_FLOW_SDK_ROUTES,
  PhAuthGuard,
  ScopesGuard,
} from '@frontend/common/ph-auth';
import { CustomerResolver, DepartmentsAvailableResolver } from '@frontend/entity';
import { AccountDisableGuard } from '@frontend/ui/account-disable';
import {
  RecipientDialogComponent,
  RecipientsListComponent,
} from '@frontend/ui/patient-recipients/src';
import { AppContentWrapperComponent } from './app-content-wrapper/app-content-wrapper.component';
import { RoutedDepartmentSelectorComponent } from './app-content-wrapper/department-selector/routed.component';
import { AppCurrentDepartmentResolver } from './app-current-department.resolver';
import { AppGuard } from './app.guard';
import { DepartmentSelectedGuard } from './department-selected.guard';
import { GraphQlGuard } from './graphql.guard';

const routes: Routes = [
  ...AUTH_FLOW_LOGOUT_ROUTES,
  ...AUTH_FLOW_SDK_ROUTES,
  {
    path: '',
    data: {
      requiredScopes: ['access:wrt'],
    },
    canActivate: [AccountDisableGuard, PhAuthGuard, ScopesGuard, GraphQlGuard, AppGuard],
    resolve: {
      customerLoaded: CustomerResolver,
      departmentsLoaded: DepartmentsAvailableResolver,
    },
    children: [
      {
        path: 'department-selector',
        canActivate: [DepartmentSelectedGuard],
        component: RoutedDepartmentSelectorComponent,
      },
      {
        path: 'configuration',
        component: AppContentWrapperComponent,
        children: [
          {
            path: 'departments',
            canActivate: [ScopesGuard],
            data: { requiredScopes: ['ui:ca:wrt-departments'] },
            loadChildren: () =>
              import('../app/configuration/workflow-departments-section').then(
                (m) => m.WorkflowDepartmentsSectionModule
              ),
          },
          {
            path: 'departments/:department_id/workflows',
            canActivate: [ScopesGuard],
            data: { requiredScopes: ['ui:ca:wrt-workflows'] },
            loadChildren: () =>
              import('../app/configuration/workflows-section').then(
                (m) => m.WorkflowsSectionModule
              ),
          },
          {
            path: 'departments/:department_id/workflows/:workflow_id/phases',
            canActivate: [ScopesGuard],
            data: { requiredScopes: ['ui:ca:wrt-phases'] },
            loadChildren: () =>
              import('../app/configuration/phases-section').then(
                (m) => m.PhasesSectionModule
              ),
          },
          {
            path: 'departments/:department_id/display-boards',
            canActivate: [ScopesGuard],
            data: { requiredScopes: ['ui:ca:wrt-display-boards'] },
            loadChildren: () =>
              import('../app/configuration/display-boards-section').then(
                (m) => m.DisplayBoardsSectionModule
              ),
          },
          {
            path: 'departments/:department_id/notifications',
            canActivate: [ScopesGuard],
            data: { requiredScopes: ['ui:ca:wrt-alerts'] },
            loadChildren: () =>
              import('../app/configuration/alerts-section').then(
                (m) => m.AlertsSectionModule
              ),
          },
          {
            path: 'departments/:department_id/quick-messages',
            canActivate: [ScopesGuard],
            data: { requiredScopes: ['ui:ca:wrt-quick-messages'] },
            loadChildren: () =>
              import('../app/configuration/quick-messages-section').then(
                (m) => m.QuickMessagesSectionModule
              ),
          },
          {
            path: 'departments/:department_id/stations',
            canActivate: [ScopesGuard],
            data: { requiredScopes: ['ui:ca:wrt-stations'] },
            loadChildren: () =>
              import('../app/configuration/stations-section').then(
                (m) => m.StationsSectionModule
              ),
          },
          {
            path: 'departments/:department_id/kiosks',
            canActivate: [ScopesGuard],
            data: { requiredScopes: ['ui:ca:wrt-kiosks'] },
            loadChildren: () =>
              import('../app/configuration/kiosks-section').then(
                (m) => m.KiosksSectionModule
              ),
          },
          {
            path: 'departments/:department_id/appointment-types',
            canActivate: [ScopesGuard],
            data: { requiredScopes: ['ui:ca:wrt-appointment-types'] },
            loadChildren: () =>
              import('../app/configuration/appointment-types-section').then(
                (m) => m.AppointmentTypesSectionModule
              ),
          },
        ],
      },
      {
        path: ':department_id',
        resolve: { currentDepartment: AppCurrentDepartmentResolver },
        component: AppContentWrapperComponent,
        children: [
          {
            path: 'patients',
            loadChildren: () =>
              import('@frontend/workflow-rt/feature/patients').then(
                (m) => m.WorkflowRtFeaturePatientsModule
              ),
          },
          {
            path: 'map',
            canActivate: [ScopesGuard],
            data: {
              requiredScopes: ['ui:wrt:map'],
            },
            loadChildren: () =>
              import('@frontend/workflow-rt/feature/maps').then(
                (m) => m.WorkflowRtFeatureMapsModule
              ),
          },
          {
            path: 'staff',
            loadChildren: () =>
              import('@frontend/workflow-rt/feature/staff').then(
                (m) => m.WorkflowRtFeatureStaffModule
              ),
          },
          {
            path: 'today-stats',
            loadChildren: () =>
              import('@frontend/workflow-rt/feature/today-stats').then(
                (m) => m.WorkflowRtFeatureTodayStatsModule
              ),
          },
          {
            path: 'rooms',
            loadChildren: () =>
              import('@frontend/workflow-rt/feature/rooms').then(
                (m) => m.WorkflowRtFeatureRoomsModule
              ),
          },
          {
            path: 'contact-tracing',
            loadChildren: () =>
              import('@frontend/workflow-rt/feature/contact-tracing').then(
                (m) => m.WorkflowRtFeatureContactTracingModule
              ),
          },
          {
            path: 'reports',
            loadChildren: () =>
              import('@frontend/workflow-rt/feature/reports').then(
                (m) => m.WorkflowRtFeatureReportsModule
              ),
          },
          {
            path: 'patients/:patient_id/recipients',
            component: RecipientsListComponent,
            children: [
              {
                path: 'new',
                component: RecipientDialogComponent,
                data: {
                  isEditOperation: false,
                },
              },
              {
                path: ':recipient_id',
                component: RecipientDialogComponent,
                data: {
                  isEditOperation: true,
                },
              },
            ],
          },
          { path: '**', redirectTo: 'patients', pathMatch: 'full' },
        ],
      },
      { path: '**', redirectTo: 'department-selector', pathMatch: 'full' },
    ],
  },
];

@NgModule({
  providers: [AppCurrentDepartmentResolver, DepartmentSelectedGuard],
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
